import React from "react";
import { X, ShieldCheck, Headphones, Trash2 } from "lucide-react";
import { useAudioClips } from "../context/AudioClipsContext";

/**
 * ClipConsentDialog — consenso esplicito separato per la registrazione
 * delle clip audio. Mostrato prima della prima sessione utile (Bedtime o
 * prima apertura dopo permesso microfono), oppure dalle Impostazioni.
 *
 * Props:
 *   open: bool - controlla apertura
 *   onClose: function - invocato dopo decisione
 */
export default function ClipConsentDialog({ open, onClose }) {
  const { grantConsent, revokeConsent } = useAudioClips();

  if (!open) return null;

  const accept = () => {
    grantConsent();
    onClose?.();
  };
  const reject = () => {
    revokeConsent();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="clip-consent-dialog"
    >
      <div className="w-full sm:w-[min(440px,92vw)] bg-[#0e1517] border border-white/10 sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-4 flex items-start gap-3 border-b border-white/5">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#14c2a1]/15 text-[#67e8d1] flex items-center justify-center">
            <Headphones size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-[16px] font-semibold leading-tight">
              Registrazione clip audio
            </div>
            <div className="text-white/55 text-[11.5px] mt-0.5 uppercase tracking-wider">
              Consenso esplicito richiesto
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 flex items-center justify-center shrink-0"
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3.5">
          <p className="text-white/90 text-[13.5px] leading-relaxed">
            Vuoi che SmartDream salvi brevi clip audio dei momenti significativi
            della notte per riascoltarli?
          </p>

          <ul className="space-y-2 text-[12.5px] text-white/75">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14c2a1] mt-1.5 shrink-0" />
              <span>
                Le clip restano <strong className="text-white">solo sul tuo dispositivo</strong> e non vengono
                caricate su server.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14c2a1] mt-1.5 shrink-0" />
              <span>
                Vengono eliminate <strong className="text-white">automaticamente dopo 30 giorni</strong>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14c2a1] mt-1.5 shrink-0" />
              <span>
                Puoi cancellarle quando vuoi dalle Impostazioni o revocare il consenso.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14c2a1] mt-1.5 shrink-0" />
              <span>
                Se rifiuti, il microfono continuerà a rilevare i dati aggregati
                (russamento, tosse, rumore) ma non salverà nessuna clip.
              </span>
            </li>
          </ul>

          <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2.5 flex items-center gap-2 text-[11px] text-white/55">
            <ShieldCheck size={14} className="text-[#67e8d1] shrink-0" />
            Dati trattati in base a consenso esplicito GDPR — categoria "audio personale sensibile".
          </div>
        </div>

        <div className="px-5 pb-5 grid grid-cols-2 gap-2">
          <button
            onClick={reject}
            data-testid="clip-consent-reject"
            className="h-11 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-[13px] font-medium flex items-center justify-center gap-1.5 transition-colors"
          >
            <Trash2 size={13} /> Rifiuta
          </button>
          <button
            onClick={accept}
            data-testid="clip-consent-accept"
            className="h-11 rounded-xl text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-all"
            style={{
              background: "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
              boxShadow: "0 8px 20px -10px rgba(20,194,161,0.5)",
            }}
          >
            Accetta
          </button>
        </div>
      </div>
    </div>
  );
}
